import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { postsAPI } from '../../lib/api';
import Layout from '../../components/layout/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Calendar, 
  Search, 
  ArrowLeft,
  User
} from 'lucide-react';

// Posts List Page
export const AnnouncementsPage = () => {
  const { t, lang, isRTL } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const params = { status: 'published' };
        if (category) params.category = category;
        const data = await postsAPI.getAll(params);
        setPosts(data);
      } catch (error) {
        console.error('Failed to fetch posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [category]);

  const getLocalizedField = (item, field) => {
    return item[`${field}_${lang}`] || item[`${field}_ro`] || item[`${field}_en`] || '';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === 'ar' ? 'ar-SA' : lang === 'en' ? 'en-GB' : 'ro-RO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const filteredPosts = posts.filter((post) => {
    const title = getLocalizedField(post, 'title').toLowerCase();
    const content = getLocalizedField(post, 'content').toLowerCase();
    return title.includes(searchTerm.toLowerCase()) || content.includes(searchTerm.toLowerCase());
  });

  const categories = ['announcement', 'event', 'news', 'education'];

  return (
    <Layout>
      {/* Header */}
      <section className="bg-islamic-teal text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2">
            {t('nav.announcements')}
          </h1>
          <p className="text-white/70">{t('home.latestNews')}</p>
        </div>
      </section>

      <section className="py-8 md:py-12 bg-islamic-pearl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="relative flex-grow max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                type="text"
                placeholder={t('common.search') + '...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={category === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategory('')}
                className={category === '' ? 'bg-islamic-teal' : ''}
                data-testid="filter-all"
              >
                {t('common.filter')}: All
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={category === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategory(cat)}
                  className={category === cat ? 'bg-islamic-teal' : ''}
                  data-testid={`filter-${cat}`}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>

          {/* Posts Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="border-stone-200">
                  <CardContent className="p-0">
                    <div className="h-48 bg-stone-200 animate-pulse"></div>
                    <div className="p-6">
                      <div className="h-4 bg-stone-200 rounded animate-pulse mb-3 w-1/4"></div>
                      <div className="h-6 bg-stone-200 rounded animate-pulse mb-2"></div>
                      <div className="h-4 bg-stone-200 rounded animate-pulse w-3/4"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post) => (
                <Link key={post.id} to={`/annonces/${post.id}`} data-testid={`post-${post.id}`}>
                  <Card className="h-full card-hover border-stone-200 bg-white overflow-hidden group">
                    <CardContent className="p-0">
                      {post.cover_image ? (
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={post.cover_image} 
                            alt={getLocalizedField(post, 'title')}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      ) : (
                        <div className="h-48 bg-gradient-to-br from-islamic-teal/10 to-islamic-gold/10 flex items-center justify-center">
                          <span className="font-arabic text-4xl text-islamic-teal/30">ر</span>
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-2 text-xs text-stone-500 mb-3">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.created_at)}
                          <span className="px-2 py-0.5 bg-stone-100 rounded text-stone-600 capitalize">
                            {post.category}
                          </span>
                        </div>
                        <h3 className="font-semibold text-lg mb-2 text-stone-800 line-clamp-2 group-hover:text-islamic-teal transition-colors">
                          {getLocalizedField(post, 'title')}
                        </h3>
                        <p className="text-sm text-stone-600 line-clamp-3">
                          {getLocalizedField(post, 'excerpt') || getLocalizedField(post, 'content').substring(0, 150)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="border-stone-200">
              <CardContent className="p-12 text-center">
                <p className="text-stone-500">{t('common.noResults')}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </Layout>
  );
};

// Single Post Page
export const AnnouncementDetailPage = () => {
  const { id } = useParams();
  const { t, lang, isRTL } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const data = await postsAPI.getById(id);
        setPost(data);
      } catch (error) {
        console.error('Failed to fetch post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  const getLocalizedField = (item, field) => {
    if (!item) return '';
    return item[`${field}_${lang}`] || item[`${field}_ro`] || item[`${field}_en`] || '';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === 'ar' ? 'ar-SA' : lang === 'en' ? 'en-GB' : 'ro-RO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-stone-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-stone-200 rounded w-1/4 mb-8"></div>
            <div className="h-64 bg-stone-200 rounded mb-8"></div>
            <div className="space-y-3">
              <div className="h-4 bg-stone-200 rounded"></div>
              <div className="h-4 bg-stone-200 rounded"></div>
              <div className="h-4 bg-stone-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-semibold text-stone-800 mb-4">Post not found</h1>
          <Link to="/annonces">
            <Button variant="outline">{t('common.back')}</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <article className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link to="/annonces" className="inline-flex items-center gap-2 text-islamic-teal hover:text-islamic-teal-light mb-6" data-testid="back-link">
            <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            {t('common.back')}
          </Link>

          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 text-sm text-stone-500 mb-4">
              <span className="px-3 py-1 bg-islamic-teal/10 text-islamic-teal rounded-full capitalize">
                {post.category}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {post.author_name}
              </span>
            </div>
            <h1 className="font-serif text-3xl md:text-4xl font-bold text-stone-800 mb-4">
              {getLocalizedField(post, 'title')}
            </h1>
            {getLocalizedField(post, 'excerpt') && (
              <p className="text-lg text-stone-600 leading-relaxed">
                {getLocalizedField(post, 'excerpt')}
              </p>
            )}
          </header>

          {/* Cover image */}
          {post.cover_image && (
            <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
              <img 
                src={post.cover_image} 
                alt={getLocalizedField(post, 'title')}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Content */}
          <div 
            className={`prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-stone-800 prose-p:text-stone-600 prose-a:text-islamic-teal ${isRTL ? 'font-arabic' : ''}`}
            dangerouslySetInnerHTML={{ __html: getLocalizedField(post, 'content').replace(/\n/g, '<br/>') }}
          />
        </div>
      </article>
    </Layout>
  );
};

export default AnnouncementsPage;
